import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Check, CheckCheck, Loader2, Search } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { cn, ConfirmDialog, showToast } from '@my/ui';
import { useLocations, useOrganisation, useParties } from '@my/directory-api/hooks';
import type { Address, Party } from '@my/directory-api';
import { useApproveOrdersForInvoicing, useOrdersForApproval } from '@my/tms-api/hooks';
import type { ApprovalException, OrderForApproval } from '@my/tms-api';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { parseOrderReferenceLabels } from '@/utils/orderReferenceLabels';
import { useInvoiceViewStore } from '@/stores/invoiceView';
import { exceptionLabel, pickPrimaryException } from '../lib/approval';
import { AssistantButton, CustomerGroupHeader, GroupToggle, StatusDot, ToolbarDivider } from './shared';

/**
 * For-approval tab — orders that are Completed and still Pending invoicing
 * (live from `GET /orders/for-approval`). Each row carries its charge totals
 * and the server-computed approval exceptions (warn/stop). Customer + address
 * ids are resolved to display text via the Directory; reference exceptions are
 * labelled with the org's custom reference names. Clean rows approve directly,
 * warnings approve via an override confirm, stoppers are blocked.
 */
export function ForApprovalTab({ onOpenAssistant }: { onOpenAssistant: () => void }) {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const navigate = useNavigate();
  const openOrder = (id: string) => navigate(`/orders/${id}`);

  const ordersQuery = useOrdersForApproval(orgId || null);
  const customersQuery = useParties(orgId, 'Customer');
  const locationsQuery = useLocations(orgId);
  const organisationQuery = useOrganisation(orgId);
  const approveMutation = useApproveOrdersForInvoicing();

  const grouped = useInvoiceViewStore((s) => s.grouped);
  const setGrouped = useInvoiceViewStore((s) => s.setGrouped);
  const [search, setSearch] = useState('');
  const [confirmRow, setConfirmRow] = useState<ApprovalRow | null>(null);

  const refLabels = useMemo(
    () => parseOrderReferenceLabels(organisationQuery.data?.tmsOrderReferenceLabelsJson),
    [organisationQuery.data?.tmsOrderReferenceLabelsJson],
  );

  const customersById = useMemo(() => {
    const map = new Map<string, Party>();
    for (const p of customersQuery.data ?? []) map.set(p.id, p);
    return map;
  }, [customersQuery.data]);

  const addressById = useMemo(() => {
    const map = new Map<string, Address>();
    for (const a of locationsQuery.data ?? []) map.set(a.id, a);
    return map;
  }, [locationsQuery.data]);

  const rows = useMemo(
    () => (ordersQuery.data ?? []).map((o) => buildRow(o, customersById, addressById, refLabels)),
    [ordersQuery.data, customersById, addressById, refLabels],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.orderNumber.toLowerCase().includes(q) ||
        r.customerName.toLowerCase().includes(q) ||
        r.deliveryPoint.toLowerCase().includes(q),
    );
  }, [rows, search]);

  // Bulk "Approve all eligible" only sweeps fully-clean rows — warnings must be
  // overridden one at a time, stoppers can't be approved at all.
  const eligible = filtered.filter((r) => r.exceptions.length === 0);

  const approve = (ids: string[]) => {
    if (ids.length === 0 || !orgId) return;
    approveMutation.mutate(
      { organisationId: orgId, orderIds: ids },
      {
        onSuccess: (res) => {
          if (res.approved.length > 0)
            showToast({
              type: 'success',
              message: `Approved ${res.approved.length} order${res.approved.length === 1 ? '' : 's'} for invoicing.`,
            });
          if (res.rejected.length > 0)
            showToast({
              type: 'error',
              message: `${res.rejected.length} order${res.rejected.length === 1 ? '' : 's'} could not be approved.`,
            });
        },
        onError: () => showToast({ type: 'error', message: 'Could not approve — please try again.' }),
      },
    );
  };

  const groups = useMemo(() => {
    const map = new Map<string, ApprovalRow[]>();
    for (const r of filtered) {
      const arr = map.get(r.customerId);
      if (arr) arr.push(r);
      else map.set(r.customerId, [r]);
    }
    return [...map.values()];
  }, [filtered]);

  // Flat view adds a Customer column (no group header to carry the name).
  const colCount = grouped ? 14 : 15;
  const busy = ordersQuery.isPending || customersQuery.isPending || locationsQuery.isPending;
  const loadError = ordersQuery.error ?? customersQuery.error ?? locationsQuery.error ?? null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden pb-4">
      <div className="flex flex-wrap items-center justify-between gap-2 shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search orders…"
            className="w-64 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
          />
        </div>
        <div className="flex items-center gap-2">
          <GroupToggle grouped={grouped} onChange={setGrouped} />
          <ToolbarDivider />
          <button
            type="button"
            onClick={() => approve(eligible.map((r) => r.id))}
            disabled={eligible.length === 0 || approveMutation.isPending}
            className="inline-flex items-center gap-1 rounded-sm bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Approve all eligible
          </button>
          <AssistantButton onClick={onOpenAssistant} />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead>
            <tr
              className={cn(
                'sticky top-0 z-10 text-[11px] uppercase tracking-wide',
                grouped
                  ? 'border-b border-slate-300 bg-slate-50 text-slate-400'
                  : 'bg-slate-700 text-white',
              )}
            >
              <Th className="w-8" />
              <Th>Order</Th>
              {!grouped && <Th>Customer</Th>}
              <Th>Acc</Th>
              <Th>Collection point</Th>
              <Th>Delivery point</Th>
              <Th>Collected</Th>
              <Th>Delivered</Th>
              <Th>Cargo</Th>
              <Th className="text-right">Transport</Th>
              <Th className="text-right">Fuel</Th>
              <Th className="text-right">Other</Th>
              <Th className="text-right">Total</Th>
              <Th>Refs</Th>
              <Th className="text-right">Action</Th>
            </tr>
          </thead>
          <tbody>
            {!busy &&
              (grouped
                ? groups.map((g) => (
                    <CustomerGroup
                      key={g[0]!.customerId}
                      rows={g}
                      onApprove={approve}
                      onOverride={setConfirmRow}
                      onOpen={openOrder}
                    />
                  ))
                : filtered.map((r) => (
                    <OrderRow
                      key={r.id}
                      row={r}
                      onApprove={approve}
                      onOverride={setConfirmRow}
                      onOpen={openOrder}
                      showCustomer
                    />
                  )))}
            {busy && (
              <tr>
                <td colSpan={colCount} className="px-3 py-10 text-center text-xs text-slate-400">
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden="true" />
                  Loading orders…
                </td>
              </tr>
            )}
            {!busy && loadError && (
              <tr>
                <td colSpan={colCount} className="px-3 py-10 text-center text-xs text-red-600">
                  Couldn’t load the approval queue — {loadError.message}
                </td>
              </tr>
            )}
            {!busy && !loadError && filtered.length === 0 && (
              <tr>
                <td colSpan={colCount} className="px-3 py-10 text-center text-xs italic text-slate-400">
                  Nothing waiting for approval.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={confirmRow !== null}
        title={`Approve ${confirmRow?.orderNumber ?? ''}?`}
        message={
          <>
            This order has {confirmRow && confirmRow.warnings.length > 1 ? 'warnings' : 'a warning'} —{' '}
            <span className="font-medium text-slate-900">
              {confirmRow?.warnings.map((w) => `“${w.label}”`).join(', ')}
            </span>
            . Approve it for invoicing anyway?
          </>
        }
        confirmLabel="Approve anyway"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (confirmRow) approve([confirmRow.id]);
          setConfirmRow(null);
        }}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
}

function CustomerGroup({
  rows,
  onApprove,
  onOverride,
  onOpen,
}: {
  rows: ApprovalRow[];
  onApprove: (ids: string[]) => void;
  onOverride: (row: ApprovalRow) => void;
  onOpen: (id: string) => void;
}) {
  const c = rows[0]!;
  // Per-customer "Approve eligible" sweeps only fully-clean rows (same rule as
  // the toolbar's "Approve all eligible").
  const eligibleIds = rows.filter((r) => r.exceptions.length === 0).map((r) => r.id);
  const sum = (pick: (r: ApprovalRow) => number) => rows.reduce((s, r) => s + pick(r), 0);
  return (
    <>
      <tr>
        <td colSpan={14} className="p-0">
          <CustomerGroupHeader
            name={c.customerName}
            code={c.customerCode}
            action={
              <button
                type="button"
                onClick={() => onApprove(eligibleIds)}
                disabled={eligibleIds.length === 0}
                aria-label="Approve eligible"
                title="Approve eligible"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-emerald-300 hover:bg-white/10 hover:text-emerald-200 disabled:opacity-30"
              >
                <CheckCheck className="h-4 w-4" />
              </button>
            }
          />
        </td>
      </tr>
      {rows.map((r) => (
        <OrderRow key={r.id} row={r} onApprove={onApprove} onOverride={onOverride} onOpen={onOpen} />
      ))}
      <tr className="border-b border-slate-300 bg-slate-50/60 text-[11px] text-slate-500">
        <td />
        <td colSpan={7} className="px-3 py-1.5 italic">
          {rows.length} order{rows.length === 1 ? '' : 's'} · subtotal
        </td>
        <Money v={sum((r) => r.transport)} c={c.currency} bold />
        <Money v={sum((r) => r.fuel)} c={c.currency} bold />
        <Money v={sum((r) => r.other)} c={c.currency} bold />
        <Money v={sum((r) => r.total)} c={c.currency} bold />
        <td colSpan={2} />
      </tr>
    </>
  );
}

function OrderRow({
  row,
  onApprove,
  onOverride,
  onOpen,
  showCustomer,
}: {
  row: ApprovalRow;
  onApprove: (ids: string[]) => void;
  onOverride: (row: ApprovalRow) => void;
  onOpen: (id: string) => void;
  showCustomer?: boolean;
}) {
  const tone = row.stoppers.length > 0 ? 'stop' : row.warnings.length > 0 ? 'warn' : 'ok';
  const tooltip = row.exceptions.map((e) => e.label).join(', ') || undefined;
  // Show just the most-important issue to keep the row uncluttered; the rest are
  // conveyed by the +N hint, the dot tooltip, and the approve confirm dialog.
  const primary = pickPrimaryException(row.exceptions);
  const extra = row.exceptions.length - (primary ? 1 : 0);
  return (
    <tr
      onDoubleClick={() => onOpen(row.id)}
      title="Double-click to open the order"
      className="cursor-pointer select-none border-b border-slate-300 last:border-0 hover:bg-slate-50"
    >
      <td className="px-3 py-1.5 text-center">
        <StatusDot tone={tone} title={tooltip} />
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 font-medium text-slate-700">{row.orderNumber}</td>
      {showCustomer && (
        <td className="max-w-[12rem] truncate px-3 py-1.5 text-slate-700" title={row.customerName}>
          {row.customerName}
        </td>
      )}
      <td className="px-3 py-1.5 text-slate-500">{row.customerCode}</td>
      <td className="max-w-[12rem] truncate px-3 py-1.5 text-slate-700" title={row.collectionPoint}>
        {row.collectionPoint}
      </td>
      <td className="max-w-[12rem] truncate px-3 py-1.5 text-slate-700" title={row.deliveryPoint}>
        {row.deliveryPoint}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">{row.collected}</td>
      <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">{row.delivered}</td>
      <td className="max-w-[11rem] truncate px-3 py-1.5 text-slate-500" title={row.cargo}>
        {row.cargo}
      </td>
      <Money v={row.transport} c={row.currency} />
      <Money v={row.fuel} c={row.currency} />
      <Money v={row.other} c={row.currency} />
      <Money v={row.total} c={row.currency} bold />
      <td className="whitespace-nowrap px-3 py-1.5 text-slate-400">{row.reference}</td>
      <td className="px-3 py-1.5">
        <div className="flex items-center justify-end gap-2" onDoubleClick={(e) => e.stopPropagation()}>
          {/* Single most-important issue + a "+N" hint when there are more. */}
          {primary && (
            <span className="inline-flex items-center gap-1">
              <ExceptionBadge tone={primary.severity === 'Stop' ? 'stop' : 'warn'} label={primary.label} />
              {extra > 0 && <span className="text-[10px] font-medium text-slate-400">+{extra}</span>}
            </span>
          )}
          {/* Clean → direct approve; warnings-only → override approve; stoppers → no control. */}
          {row.exceptions.length === 0 ? (
            <button
              type="button"
              onClick={() => onApprove([row.id])}
              aria-label="Approve"
              title="Approve"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-50"
            >
              <Check className="h-4 w-4" />
            </button>
          ) : row.canApprove ? (
            <button
              type="button"
              onClick={() => onOverride(row)}
              aria-label="Approve despite warnings"
              title="Approve despite warnings"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-amber-600 hover:bg-amber-50"
            >
              <Check className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

function ExceptionBadge({ tone, label }: { tone: 'warn' | 'stop'; label: string }) {
  return (
    <span
      title={label}
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-1 text-[10px] font-medium',
        tone === 'stop' ? 'border-red-300 bg-red-50 text-red-700' : 'border-amber-300 bg-amber-50 text-amber-800',
      )}
    >
      {label}
    </span>
  );
}

function Money({ v, c, bold }: { v: number; c: string; bold?: boolean }) {
  return (
    <td className={cn('whitespace-nowrap px-3 py-1.5 text-right tabular-nums', bold ? 'font-semibold text-slate-800' : 'text-slate-600')}>
      {v === 0 ? '—' : formatMoney(v, c)}
    </td>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <th className={cn('px-3 py-1.5 text-left font-semibold', className)}>{children}</th>;
}

// ── View model ──────────────────────────────────────────────────────────────

interface ApprovalRow {
  id: string;
  orderNumber: string;
  customerId: string;
  customerName: string;
  customerCode: string;
  currency: string;
  collectionPoint: string;
  deliveryPoint: string;
  collected: string;
  delivered: string;
  cargo: string;
  transport: number;
  fuel: number;
  other: number;
  total: number;
  reference: string;
  exceptions: LabelledException[];
  warnings: LabelledException[];
  stoppers: LabelledException[];
  canApprove: boolean;
}

type LabelledException = ApprovalException & { label: string };

function buildRow(
  o: OrderForApproval,
  customersById: Map<string, Party>,
  addressById: Map<string, Address>,
  refLabels: string[],
): ApprovalRow {
  const customer = customersById.get(o.customerPartyId);
  const exceptions: LabelledException[] = o.exceptions.map((e) => ({ ...e, label: exceptionLabel(e, refLabels) }));
  return {
    id: o.id,
    orderNumber: o.orderNumber,
    customerId: o.customerPartyId,
    customerName: customer?.name ?? '—',
    customerCode: customer?.code ?? '—',
    // Order currency wins; fall back to the customer's, then EUR.
    currency: o.currencyIsoCode || customer?.currencyIsoCode || 'EUR',
    collectionPoint: formatAddress(addressById.get(o.collectionAddressId ?? '')),
    deliveryPoint: formatAddress(addressById.get(o.deliveryAddressId ?? '')),
    collected: formatDate(o.collectedDate),
    delivered: formatDate(o.deliveredDate),
    cargo: o.cargo ?? '—',
    transport: o.transport,
    fuel: o.fuel,
    other: o.other,
    total: o.total,
    reference: o.reference1 || o.reference2 || o.reference3 || o.reference4 || '—',
    exceptions,
    warnings: exceptions.filter((e) => e.severity === 'Warn'),
    stoppers: exceptions.filter((e) => e.severity === 'Stop'),
    canApprove: o.canApprove,
  };
}

function formatAddress(a: Address | undefined): string {
  if (!a) return '—';
  const parts = [a.name?.trim(), a.city?.trim()].filter((p): p is string => !!p);
  return parts.length > 0 ? parts.join(', ') : a.fullAddress?.trim() || '—';
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

function formatMoney(v: number, iso: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: iso }).format(v);
  } catch {
    return v.toFixed(2);
  }
}
